import {
  ActionIcon,
  AppShell,
  Avatar,
  Box,
  Button,
  Group,
  Menu,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconLogout, IconMoon, IconPencil, IconPlus, IconSun, IconUser } from '@tabler/icons-react'
import React from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useOwlbertWebAuth } from './auth/OwlbertWebAuthContext'
import { fetchMe } from './api'
import { ShellLayoutProvider, ShellTopBarSlotProvider } from './shellLayout'
import { WebMobileTabBar } from './WebMobileTabBar'
import { WebNavbarItem } from './WebNavbarItem'
import { BASIC_TOP_ROUTES } from './webShellNav'

import './styles/shell.css'

/** Match Mantine `sm` (~48rem): below this we use the iOS-style bottom tab dock. */
const MOBILE_LAYOUT_QUERY = '(max-width: 47.99em)'

function initialsFromPrefix(prefix: string): string {
  const p = prefix.trim()
  if (!p) return '?'
  return p.slice(0, 2).toUpperCase()
}

export const WebBasicShell: React.FC = () => {
  const navigate = useNavigate()
  const { setColorScheme } = useMantineColorScheme()
  const computedColorScheme = useComputedColorScheme('light')
  const owlbertAuth = useOwlbertWebAuth()
  const isMobileLayout = useMediaQuery(MOBILE_LAYOUT_QUERY, false, { getInitialValueInEffect: true })

  const [sessionPrefix, setSessionPrefix] = React.useState<string>('')
  const [topBarLeft, setTopBarLeft] = React.useState<HTMLDivElement | null>(null)
  const [newWsOpen, setNewWsOpen] = React.useState(false)
  const [newWsName, setNewWsName] = React.useState('')
  const [creatingWs, setCreatingWs] = React.useState(false)
  const [renameWsOpen, setRenameWsOpen] = React.useState(false)
  const [renameWsName, setRenameWsName] = React.useState('')
  const [renamingWs, setRenamingWs] = React.useState(false)

  const handleCreateWorkspace = React.useCallback(async () => {
    const name = newWsName.trim()
    if (!name) return
    const bundle = owlbertAuth.bundle
    if (!bundle) {
      notifications.show({ color: 'yellow', message: 'Sign in first.' })
      return
    }
    setCreatingWs(true)
    try {
      const created = await bundle.client.owlbertHosted.workspaces.create.mutate({ name })
      await owlbertAuth.refreshWorkspaces()
      owlbertAuth.setWorkspaceId(created.id)
      setNewWsOpen(false)
      setNewWsName('')
      notifications.show({ color: 'teal', message: `Switched to “${created.name}”.` })
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Could not create workspace',
        message: String((e as Error)?.message ?? e),
      })
    } finally {
      setCreatingWs(false)
    }
  }, [newWsName, owlbertAuth])

  const handleRenameWorkspace = React.useCallback(async () => {
    const name = renameWsName.trim()
    const id = owlbertAuth.workspaceId
    const bundle = owlbertAuth.bundle
    if (!name || !id || !bundle) return
    setRenamingWs(true)
    try {
      await bundle.client.owlbertHosted.workspaces.update.mutate({ id, name })
      await owlbertAuth.refreshWorkspaces()
      setRenameWsOpen(false)
      notifications.show({ color: 'teal', message: `Renamed workspace to “${name}”.` })
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Could not rename workspace',
        message: String((e as Error)?.message ?? e),
      })
    } finally {
      setRenamingWs(false)
    }
  }, [renameWsName, owlbertAuth])

  const openRenameWorkspace = React.useCallback(() => {
    const active = owlbertAuth.workspaces.find((w) => w.id === owlbertAuth.workspaceId)
    setRenameWsName(active?.name ?? '')
    setRenameWsOpen(true)
  }, [owlbertAuth.workspaces, owlbertAuth.workspaceId])

  const renameWorkspaceModal = (
    <Modal opened={renameWsOpen} onClose={() => setRenameWsOpen(false)} title="Rename workspace" centered>
      <Stack gap="md">
        <TextInput
          label="Workspace name"
          data-autofocus
          value={renameWsName}
          onChange={(e) => setRenameWsName(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && renameWsName.trim() && !renamingWs) void handleRenameWorkspace()
          }}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setRenameWsOpen(false)}>
            Cancel
          </Button>
          <Button loading={renamingWs} disabled={!renameWsName.trim()} onClick={() => void handleRenameWorkspace()}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  )

  const newWorkspaceModal = (
    <Modal opened={newWsOpen} onClose={() => setNewWsOpen(false)} title="New workspace" centered>
      <Stack gap="md">
        <TextInput
          label="Workspace name"
          placeholder="e.g. Personal, Work, Research"
          data-autofocus
          value={newWsName}
          onChange={(e) => setNewWsName(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newWsName.trim() && !creatingWs) void handleCreateWorkspace()
          }}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setNewWsOpen(false)}>
            Cancel
          </Button>
          <Button loading={creatingWs} disabled={!newWsName.trim()} onClick={() => void handleCreateWorkspace()}>
            Create & switch
          </Button>
        </Group>
      </Stack>
    </Modal>
  )

  React.useEffect(() => {
    void fetchMe()
      .then((m) => setSessionPrefix(m.sessionIdPrefix))
      .catch(() => setSessionPrefix(''))
  }, [])

  const sessionLabel =
    owlbertAuth.sessionReady && owlbertAuth.user?.email
      ? owlbertAuth.user.email
      : owlbertAuth.sessionReady
        ? owlbertAuth.user?.id ?? ''
        : sessionPrefix
          ? `Session ${sessionPrefix}…`
          : ''

  const workspaceControl =
    owlbertAuth.sessionReady && owlbertAuth.workspaces.length > 0 ? (
      <Group gap={4} wrap="nowrap">
        <Tooltip label="Active workspace" position="bottom">
          <Select
            aria-label="Active workspace"
            size="xs"
            radius="xl"
            w={170}
            comboboxProps={{ withinPortal: true }}
            checkIconPosition="right"
            allowDeselect={false}
            data={owlbertAuth.workspaces.map((w) => ({ value: w.id, label: w.name }))}
            value={owlbertAuth.workspaceId ?? undefined}
            onChange={(v) => {
              if (v) owlbertAuth.setWorkspaceId(v)
            }}
          />
        </Tooltip>
        <Tooltip label="Rename workspace" position="bottom">
          <ActionIcon
            variant="subtle"
            size="sm"
            radius="xl"
            aria-label="Rename workspace"
            disabled={!owlbertAuth.workspaceId}
            onClick={openRenameWorkspace}
          >
            <IconPencil size={16} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="New workspace" position="bottom">
          <ActionIcon
            variant="subtle"
            size="sm"
            radius="xl"
            aria-label="New workspace"
            onClick={() => setNewWsOpen(true)}
          >
            <IconPlus size={18} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
      </Group>
    ) : null

  const themeButton = (
    <Tooltip label={computedColorScheme === 'dark' ? 'Light mode' : 'Dark mode'} position="bottom">
      <ActionIcon
        variant="subtle"
        size="sm"
        aria-label={computedColorScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        onClick={() => setColorScheme(computedColorScheme === 'dark' ? 'light' : 'dark')}
      >
        {computedColorScheme === 'dark' ? <IconSun size={18} stroke={1.5} /> : <IconMoon size={18} stroke={1.5} />}
      </ActionIcon>
    </Tooltip>
  )

  // Floating cluster used only on mobile (no full-width top bar there).
  const floatingControls = (
    <Group gap="xs" className="owlbert-web-theme-toggle" style={{ position: 'fixed', zIndex: 1000 }}>
      {workspaceControl}
      {themeButton}
    </Group>
  )

  if (isMobileLayout) {
    return (
      <ShellLayoutProvider value={{ navWidth: 0, iconOnly: true }}>
        {floatingControls}
        {newWorkspaceModal}
        {renameWorkspaceModal}
        <Box className="owlbert-web-mobile-shell">
          <Box className="owlbert-web-mobile-shell-body">
            <Outlet />
          </Box>
          <WebMobileTabBar />
        </Box>
      </ShellLayoutProvider>
    )
  }

  return (
    <ShellLayoutProvider value={{ navWidth: 0, iconOnly: false }}>
      <ShellTopBarSlotProvider value={topBarLeft}>
      {newWorkspaceModal}
      {renameWorkspaceModal}
      <AppShell
        className="owlbert-app-shell"
        header={{ height: 48 }}
        aside={{ width: '0px', breakpoint: 'sm', collapsed: { desktop: true, mobile: true } }}
        footer={{ height: 0 }}
        style={{ zIndex: 900, height: '100%' }}
      >
        <AppShell.Header className="owlbert-web-top-bar">
          <Group h="100%" px="sm" justify="space-between" wrap="nowrap" gap="sm">
            <Group gap="md" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
              <Group
                gap={8}
                wrap="nowrap"
                align="center"
                style={{ cursor: 'pointer', flexShrink: 0 }}
                onClick={() => navigate('/assistants')}
              >
                <img
                  src="/owlbert-pixels.png"
                  alt="Owlbert"
                  style={{ height: 28, width: 'auto', display: 'block' }}
                />
                <Text fw={700} size="sm" style={{ userSelect: 'none' }}>
                  Owlbert.ai
                </Text>
              </Group>
              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                {BASIC_TOP_ROUTES.map((r) => (
                  <WebNavbarItem
                    key={r.path}
                    onClick={() => {}}
                    icon={r.icon}
                    label={r.label}
                    hint={r.hint}
                    path={r.path}
                  />
                ))}
              </Group>
              <Box ref={setTopBarLeft} style={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 8 }} />
            </Group>
            <Group gap="xs" wrap="nowrap">
              {workspaceControl}
              {themeButton}
              {sessionLabel ? (
                <Menu position="bottom-end" withinPortal shadow="md" width={220}>
                  <Menu.Target>
                    <Avatar
                      radius="xl"
                      size={30}
                      color={computedColorScheme === 'dark' ? 'dark.3' : 'gray.5'}
                      style={{
                        flexShrink: 0,
                        cursor: 'pointer',
                        border: '2px solid var(--mantine-color-default-border)',
                      }}
                    >
                      {initialsFromPrefix(
                        owlbertAuth.sessionReady ? owlbertAuth.user?.email ?? owlbertAuth.user?.id ?? '' : sessionPrefix,
                      )}
                    </Avatar>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label style={{ wordBreak: 'break-all' }}>{sessionLabel}</Menu.Label>
                    <Menu.Item leftSection={<IconUser size={14} />} onClick={() => navigate('/account')}>
                      Account
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      color="red"
                      leftSection={<IconLogout size={14} />}
                      onClick={() => void owlbertAuth.logout()}
                    >
                      Sign out
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              ) : null}
            </Group>
          </Group>
        </AppShell.Header>
        <AppShell.Main
          className="owlbert-app-shell-main"
          style={{ height: '100%', maxHeight: '100%', minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Outlet />
          </Box>
        </AppShell.Main>
      </AppShell>
      </ShellTopBarSlotProvider>
    </ShellLayoutProvider>
  )
}
